import { DEFAULT_PERIOD, PeriodOption } from '@/config/model';
import { getPeriodOptionsForModel } from '@/utils/model';
import moment from 'moment';

const validatePeriodParam = (
    periodFromSearch: string | undefined,
    fallBackPeriod: PeriodOption,
    availablePeriods: PeriodOption[],
): PeriodOption | undefined => {
    if (
        !periodFromSearch ||
        !availablePeriods.includes(periodFromSearch as PeriodOption)
    ) {
        if (availablePeriods.includes(DEFAULT_PERIOD)) {
            return DEFAULT_PERIOD;
        } else {
            return fallBackPeriod;
        }
    }
};

const validateDateParam = (date: string | undefined) => {
    const shouldReturnBoundaryDate =
        !date || !moment.utc(date, 'DD-MM-YYYY').isValid();

    return {
        shouldBeBefore: (boundaryDate: moment.Moment) => {
            if (
                shouldReturnBoundaryDate ||
                boundaryDate.isBefore(moment.utc(date, 'DD-MM-YYYY'))
            )
                return boundaryDate.format('DD-MM-YYYY');
        },
        shouldBeAfter: (boundaryDate: moment.Moment) => {
            if (
                shouldReturnBoundaryDate ||
                boundaryDate.isAfter(moment.utc(date, 'DD-MM-YYYY'))
            )
                return boundaryDate.format('DD-MM-YYYY');
        },
    };
};

type SearchParams = {
    startDate?: string;
    endDate?: string;
    period?: PeriodOption;
};

export const validateDateAndPeriodSearchParams = ({
    search,
    project,
}: {
    search: SearchParams;
    project: {
        period: PeriodOption;
        startDate: string;
        endDate: string;
    };
}): { shouldRedirect: boolean; newSearch: SearchParams } => {
    const availablePeriods = getPeriodOptionsForModel(project.period);

    const period = validatePeriodParam(
        search.period,
        project.period,
        availablePeriods,
    );

    let shouldRedirect = false;
    const newSearch: SearchParams = { ...search };

    if (period) {
        shouldRedirect = true;
        newSearch.period = period;
    }

    const validatedDates = validateDateParams({
        boundaryStartDate: project.startDate,
        boundaryEndDate: project.endDate,
        endDate: search.endDate,
        startDate: search.startDate,
    });

    if (validatedDates) {
        shouldRedirect = true;
        newSearch.startDate = validatedDates.startDate;
        newSearch.endDate = validatedDates.endDate;
    }

    return { shouldRedirect, newSearch };
};

export const validateDateParams = ({
    boundaryStartDate,
    boundaryEndDate,
    endDate,
    startDate,
}: {
    startDate?: string | undefined;
    endDate?: string | undefined;
    boundaryStartDate: string;
    boundaryEndDate: string;
}) => {
    const projectStartDate = moment.utc(boundaryStartDate, 'DD-MM-YYYY');
    const projectEndDate = moment.utc(boundaryEndDate, 'DD-MM-YYYY');

    const momentEnd = moment(endDate, 'DD-MM-YYYY');

    const startDateBeforeEndDate = moment(startDate, 'DD-MM-YYYY').isSame(
        momentEnd,
    )
        ? undefined
        : validateDateParam(startDate).shouldBeBefore(momentEnd);

    const startDateAfter =
        validateDateParam(startDate).shouldBeAfter(projectStartDate);

    const startDateBefore =
        validateDateParam(startDate).shouldBeBefore(projectEndDate);

    const endDateBefore =
        validateDateParam(endDate).shouldBeBefore(projectEndDate);

    const endDateAfter =
        validateDateParam(endDate).shouldBeAfter(projectStartDate);

    if (
        startDateBeforeEndDate ||
        startDateAfter ||
        endDateBefore ||
        startDateBefore ||
        endDateAfter
    ) {
        return {
            startDate: boundaryStartDate,
            endDate: boundaryEndDate,
        };
    }

    return undefined;
};

export const validateCampaignDateParams = ({
    boundaryStartDate,
    boundaryEndDate,
    inputEndDate,
    inputStartDate,
    defaultStartDate,
    defaultEndDate,
}: {
    inputStartDate?: string | undefined;
    inputEndDate?: string | undefined;
    defaultStartDate?: string | undefined;
    defaultEndDate?: string | undefined;
    boundaryStartDate: string;
    boundaryEndDate: string;
}) => {
    const projectStartDate = moment.utc(boundaryStartDate, 'DD-MM-YYYY');
    const projectEndDate = moment.utc(boundaryEndDate, 'DD-MM-YYYY');

    const momentEnd = moment(inputEndDate, 'DD-MM-YYYY');

    const startDateBeforeEndDate = moment(inputStartDate, 'DD-MM-YYYY').isSame(
        momentEnd,
    )
        ? undefined
        : validateDateParam(inputStartDate).shouldBeBefore(momentEnd);

    const startDateAfter =
        validateDateParam(inputStartDate).shouldBeAfter(projectStartDate);

    const startDateBefore =
        validateDateParam(inputStartDate).shouldBeBefore(projectEndDate);

    const endDateBefore =
        validateDateParam(inputEndDate).shouldBeBefore(projectEndDate);

    const endDateAfter =
        validateDateParam(inputEndDate).shouldBeAfter(projectStartDate);

    if (
        startDateBeforeEndDate ||
        startDateAfter ||
        endDateBefore ||
        startDateBefore ||
        endDateAfter
    ) {
        const hasStartDateValidationError =
            startDateBeforeEndDate || startDateAfter || startDateBefore;
        const hasEndDateValidationError = endDateBefore || endDateAfter;

        const fallbackStartDate = defaultStartDate ?? boundaryStartDate;
        const startDate = hasStartDateValidationError
            ? fallbackStartDate
            : inputStartDate;

        const tempEndDate = moment(startDate, 'DD-MM-YYYY').add(6, 'months');
        const calculatedEndDate = tempEndDate.isAfter(projectEndDate)
            ? projectEndDate.format('DD-MM-YYYY')
            : tempEndDate.format('DD-MM-YYYY');

        const fallbackEndDate = defaultEndDate ?? calculatedEndDate;
        const endDate = hasEndDateValidationError
            ? fallbackEndDate
            : inputEndDate;

        return {
            startDate,
            endDate,
        };
    }

    return undefined;
};
